export interface ToolCall {
  type: string;
  arguments: string;
  output?: string;
  search_results?: {
    images?: string[];
    results?: { title: string; url: string; score: number; content: string }[];
  };
  code_results?: {
    text?: string;
    png?: string;
    chart?: Chart;
    charts?: Chart[];
  }[];
}

export interface Chart {
  type: string;
  title?: string;
  x_label?: string;
  y_label?: string;
  x_unit?: string;
  y_unit?: string;
  x_ticks?: number[];
  y_ticks?: number[];
  x_tick_labels?: string[];
  y_tick_labels?: string[];
  x_scale?: string;
  y_scale?: string;
  elements: ChartElement[];
}

export interface ChartElement {
  chart?: Chart;
  label: string;
  group?: string;
  value?: number;
  points?: [number, number][];
  angle?: number;
  radius?: number;
  min?: number;
  first_quartile?: number;
  median?: number;
  third_quartile?: number;
  max?: number;
  outliers?: number[];
}

export const useExecutedToolsSnippets = (tool_calls: ToolCall[]) => {
  const snippets = extractSnippets(tool_calls || []);

  const images = tool_calls?.find(
    (call) =>
      call.type === "search" &&
      Array.isArray(call.search_results?.images) &&
      call.search_results?.images?.length > 0,
  )?.search_results?.images;

  const codeResults = tool_calls
    ?.filter(
      (call) =>
        call.type === "python" &&
        Array.isArray(call.code_results) &&
        call.code_results?.length > 0,
    )
    .flatMap((call) => call.code_results)
    .filter((result) => {
      if (!result?.png || (result?.chart && result.chart.type === "")) {
        return false;
      }
      if (result?.charts && Array.isArray(result.charts)) {
        result.charts = result.charts.filter(
          (chart) =>
            chart.type !== "unknown" &&
            chart.type !== "" &&
            chart.elements &&
            chart.elements.length > 1,
        );

        if (result.charts.length === 1) {
          result.chart = result.charts[0];
          result.charts = undefined;
          return true;
        }
        return result.charts.length > 0;
      }

      return true;
    });
  return { snippets, images, codeResults };
};

type ParsedArticle = {
  title?: string;
  url?: string;
  content?: string;
  score?: number;
  domain?: string;
};

const titleRegex = /Title:\s*(.*)/;
const urlRegex = /URL:\s*(.*)/;
const contentRegex = /Content:\s*([\s\S]*?)Score:/;
const scoreRegex = /Score:\s*([\d.]+)/;

function parseSearchResults(raw: string): ParsedArticle[] {
  const blocks = raw.split(/(?=Title: )/g); // Split into article blocks
  const results: ParsedArticle[] = [];

  for (const block of blocks) {
    const titleMatch = block.match(titleRegex);
    const urlMatch = block.match(urlRegex);
    const contentMatch = block.match(contentRegex);
    const scoreMatch = block.match(scoreRegex);

    const title = titleMatch?.[1]?.trim();
    const url = urlMatch?.[1]?.trim();
    const domain = url?.split("/")[2];
    const content = contentMatch?.[1]?.trim();
    const score = scoreMatch
      ? Number.parseFloat(scoreMatch[1] ?? "0")
      : undefined;

    if (title || url || content || score !== undefined) {
      results.push({ title, url, content, score, domain });
    }
  }

  return results;
}

function extractSnippets(tool_calls: ToolCall[]): ParsedArticle[] {
  const searchResults = tool_calls.filter((call) => call.type === "search");
  if (!searchResults) {
    return [];
  }
  const output = searchResults.map((call) => call.output).join("\n");
  const results = parseSearchResults(output);

  // Remove duplicates based on URL
  const uniqueResults = results.reduce((acc: ParsedArticle[], curr) => {
    if (!acc.some((item) => item.url === curr.url)) {
      acc.push(curr);
    }
    return acc;
  }, []);

  return uniqueResults;
}

export type Snippet = ParsedArticle;
